// The public gallery directory behind /find-your-photos (and /find-your-photos/<company>): the search API's
// GET /galleries/list (api-search list.js → engines.list): 50 galleries a page, newest first, paged with
// searchAfter=<date of the last one>, filtered by country (lower-case ISO code), companyName or text.
//
// The SSR server prefetches the first page (src/lib/gallery/ssrPrefetch.ts) so the directory is real, crawlable
// <a href> cards in the HTML, and the page links to the next page (?country=gb&before=<date>) and to every
// country's listing. The client hydrates the same React Query entries, so its first render matches.

import { SEARCH_API_ENDPOINT } from "@/lib/apiDomain";
import { COUNTRIES } from "@/lib/countries";
import { countryTimeZone, toDate } from "@/lib/datetime";

export const GALLERY_LIST_ENDPOINT = `${SEARCH_API_ENDPOINT}/galleries/list`;

/** The API's page size (engines.list). A page shorter than this is the last one. */
export const DIRECTORY_PAGE_SIZE = 50;

/** The country shown when neither the URL nor the visitor's languages name one (as the page always did). */
export const DEFAULT_COUNTRY = "GB";

export interface GalleryHit {
  name: string;
  /** Epoch ms (publishedAt, else createdAt); older index entries may hold an ISO string. */
  date: number | string;
  url: string;
  companyName?: string;
  /** The photographer's country, lower case ("gb"). */
  country?: string;
  noItems?: number | null;
  mediaBackgroundPosition?: string;
  media?: { featuredImage?: string };
  plan?: string;
  hidden?: boolean;
}

export interface CountryBucket {
  key: string;
  doc_count: number;
}

export interface GalleryListPage {
  hits: GalleryHit[];
  total: number;
  /** Galleries per country (only meaningful on an unfiltered request). */
  countries?: CountryBucket[];
}

// ---------- React Query keys (shared by the SSR prefetch and the page) ----------

/** One page of a country's galleries: the newest (before = "") or those before a date. */
export const directoryKey = (country: string, before: string | null) =>
  ["findPhotos", "list", country.toLowerCase(), before ?? ""] as const;
/** Galleries per country, for the "browse by country" links. */
export const COUNTRIES_KEY = ["findPhotos", "countries"] as const;
/** The country the server rendered (from ?country= or Accept-Language), so the client's first render matches. */
export const INITIAL_COUNTRY_KEY = ["findPhotos", "initialCountry"] as const;
/** A company's first page (/find-your-photos/<company>). */
export const companyKey = (company: string) => ["findPhotos", "company", company] as const;

// ---------- URL parameters ----------

export interface DirectoryParams {
  /** Upper-case ISO country code, if the URL names a known one. */
  country: string | null;
  /** The "before" cursor (epoch ms of the last gallery on the previous page), digits only. */
  before: string | null;
}

const isCountry = (code: string) => COUNTRIES.some((c) => c.code === code);

/** ?country=gb&before=1784211096504 → { country: "GB", before: "1784211096504" }; anything else is ignored. */
export const readDirectoryParams = (search: string): DirectoryParams => {
  const q = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const c = (q.get("country") || "").trim().toUpperCase();
  const b = (q.get("before") || "").trim();
  return { country: /^[A-Z]{2}$/.test(c) && isCountry(c) ? c : null, before: /^\d{10,14}$/.test(b) ? b : null };
};

/** A directory URL: /find-your-photos?country=gb[&before=…][&lang=xx] (country first: robots.txt allows that prefix). */
export const directoryHref = (country: string, before?: string | number | null, lang?: string | null): string => {
  const parts = [`country=${country.toLowerCase()}`];
  if (before) parts.push(`before=${before}`);
  if (lang && lang !== "en") parts.push(`lang=${lang}`);
  return `/find-your-photos?${parts.join("&")}`;
};

// ---------- Country from the visitor's languages ----------

/** The first language tag with a known region ("en-GB", "ms-MY") → its country, as the page has always picked it. */
export const countryFromLanguages = (langs: readonly string[]): string | null => {
  for (const l of langs) {
    const region = l.split("-")[1];
    if (region && region.length === 2) {
      const upper = region.toUpperCase();
      if (isCountry(upper)) return upper;
    }
  }
  return null;
};

/** An Accept-Language header's tags, in order ("en-GB,en;q=0.9" → ["en-GB", "en"]). */
export const acceptLanguageTags = (header: string | null | undefined): string[] =>
  (header || "")
    .split(",")
    .map((part) => part.split(";")[0].trim())
    .filter(Boolean);

// ---------- Display ----------

/** A country's name in a language (Intl), falling back to the English name in COUNTRIES. */
export const countryLabel = (code: string | null | undefined, lang = "en"): string => {
  if (!code) return "";
  const upper = code.toUpperCase();
  const english = COUNTRIES.find((c) => c.code === upper)?.name ?? upper;
  if (lang === "en") return english;
  try {
    return new Intl.DisplayNames([lang], { type: "region" }).of(upper) || english;
  } catch {
    return english;
  }
};

/** A gallery's date ("18 Oct 2026"), in the page's language and the gallery's country time zone, so the server and
 *  the browser print exactly the same thing (formatDate uses the runtime's own locale, which they don't share). */
export const galleryDate = (hit: GalleryHit, lang = "en"): string => {
  const d = toDate(hit.date);
  if (!d) return "";
  const locale = lang === "en" ? "en-GB" : lang === "zh" ? "zh-Hans" : lang;
  const opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
  try {
    return new Intl.DateTimeFormat(locale, { ...opts, timeZone: countryTimeZone(hit.country) ?? "UTC" }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-GB", { ...opts, timeZone: "UTC" }).format(d);
  }
};

/** "Company · Country": where a gallery is from. */
export const galleryLocation = (hit: GalleryHit, lang = "en"): string =>
  [hit.companyName, countryLabel(hit.country, lang)].filter(Boolean).join(" · ");

/** The epoch-ms date of a hit, for the "before" cursor. */
export const hitCursor = (hit: GalleryHit | undefined): string | null => {
  if (!hit) return null;
  const n = typeof hit.date === "number" ? hit.date : Date.parse(String(hit.date));
  return Number.isFinite(n) ? String(Math.trunc(n)) : null;
};

// ---------- Fetching ----------

export interface ListQuery {
  country?: string | null;
  companyName?: string | null;
  text?: string | null;
  searchAfter?: string | null;
}

export const galleryListUrl = (q: ListQuery): string => {
  const params = new URLSearchParams();
  if (q.text) params.set("text", q.text);
  if (q.companyName) params.set("companyName", q.companyName);
  if (q.country) params.set("country", q.country.toLowerCase());
  if (q.searchAfter) params.set("searchAfter", q.searchAfter);
  const qs = params.toString();
  return `${GALLERY_LIST_ENDPOINT}${qs ? `?${qs}` : ""}`;
};

interface RawListResponse {
  hits?: GalleryHit[];
  total?: number;
  aggregations?: { country?: CountryBucket[] };
}

const toPage = (data: RawListResponse): GalleryListPage => ({
  hits: Array.isArray(data.hits) ? data.hits.filter((h) => h && h.url && h.name && !h.hidden) : [],
  total: typeof data.total === "number" ? data.total : 0,
  countries: data.aggregations?.country,
});

/** One page of the list. Throws on a network error or a non-2xx answer. */
export async function fetchGalleryList(q: ListQuery, init?: { signal?: AbortSignal }): Promise<GalleryListPage> {
  const res = await fetch(galleryListUrl(q), { signal: init?.signal });
  if (!res.ok) throw new Error(`galleries/list ${res.status}`);
  return toPage((await res.json()) as RawListResponse);
}

// The SSR server's short cache: the API answers with max-age=300, and a crawler walking the directory shouldn't
// cost an API call per page view. Server only (the browser has its own HTTP cache).
const SERVER_TTL_MS = 60_000;
const SERVER_CACHE_MAX = 500;
const serverCache = new Map<string, { exp: number; page: GalleryListPage }>();

/** For tests. */
export const clearGalleryListCache = () => serverCache.clear();

/** fetchGalleryList for the SSR prefetch: cached briefly, with a timeout; null on any failure (the page then
 *  renders without the data and the browser fetches it, as before). */
export async function fetchGalleryListForSsr(q: ListQuery, timeoutMs = 2500): Promise<GalleryListPage | null> {
  const key = galleryListUrl(q);
  const hit = serverCache.get(key);
  if (hit && hit.exp > Date.now()) return hit.page;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const page = await fetchGalleryList(q, { signal: ctrl.signal });
    if (serverCache.size >= SERVER_CACHE_MAX) serverCache.delete(serverCache.keys().next().value as string);
    serverCache.set(key, { exp: Date.now() + SERVER_TTL_MS, page });
    return page;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
